/**
 * The inbox's sections in display order: each row holds everything the inbox says about one.
 * `key` doubles as a PR's status (`InboxPrStatus`); `rule` is the header's tooltip. A section with
 * `hideWhenEmpty` shows only while it has PRs and opens only when the user opens it.
 */
export const INBOX_SECTIONS = [
  {
    key: 'needsYou',
    title: 'Needs you',
    rule: 'Waiting on your review, or on your changes',
    defaultOpen: true,
  },
  {
    key: 'readyToMerge',
    title: 'Ready to merge',
    rule: 'Your approved PRs with passing checks',
    defaultOpen: true,
  },
  {
    key: 'waitingOnOthers',
    title: 'Waiting on others',
    rule: 'Your PRs, waiting on reviewers, cubic or checks',
    defaultOpen: true,
  },
  {
    key: 'drafts',
    title: 'Drafts',
    rule: "Your PRs that aren't ready for review",
    defaultOpen: true,
  },
  {
    key: 'team',
    title: 'Team',
    rule: "Everyone else's open PRs that don't need your review",
    defaultOpen: false,
  },
  {
    key: 'snoozed',
    title: 'Snoozed',
    rule: 'Hidden until the time you picked',
    defaultOpen: false,
    hideWhenEmpty: true,
  },
  {
    key: 'merged',
    title: 'Recently merged',
    rule: 'The latest PRs merged in these repositories',
    defaultOpen: false,
  },
] as const;

export type InboxSection = (typeof INBOX_SECTIONS)[number];
export type InboxSectionKey = InboxSection['key'];

export const INBOX_SECTION_BY_KEY = Object.fromEntries(
  INBOX_SECTIONS.map((section) => [section.key, section]),
) as { [Key in InboxSectionKey]: Extract<InboxSection, { key: Key }> };

/**
 * A stack sits whole in the first of these sections that any of its members belongs to. Ready to
 * merge ranks below Waiting on others on purpose: a stack waits with its slowest member.
 */
export const STACK_SECTION_PRIORITY: readonly InboxSectionKey[] = [
  'needsYou',
  'waitingOnOthers',
  'readyToMerge',
  'drafts',
  'team',
  'merged',
];
